import { useEffect, useState } from "react";
import { Nav } from "../components/Nav";
import { ShortenerForm } from "../components/ShortenerForm";
import { ResultCard } from "../components/ResultCard";
import { TimeSeriesChart } from "../components/AnalyticsCharts";
import { ShareModal } from "../components/ShareModal";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "../components/ui/table";
import { Copy, Share2, Trash2, BarChart3, Check, Link as LinkIcon, MousePointerClick, Sparkles } from "lucide-react";
import { Textarea } from "../components/ui/textarea";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger,
} from "../components/ui/dialog";
import { toast } from "sonner";

export default function Dashboard() {
    const { user, loading } = useAuth();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [links, setLinks] = useState([]);
    const [result, setResult] = useState(null);
    const [analytics, setAnalytics] = useState(null);
    const [shareUrl, setShareUrl] = useState(null);
    const [copiedId, setCopiedId] = useState(null);
    const [bulkText, setBulkText] = useState("");
    const [bulkOpen, setBulkOpen] = useState(false);

    useEffect(() => {
        if (!loading && !user) navigate("/login");
    }, [user, loading, navigate]);

    const load = async () => {
        try {
            const [{ data: linksRes }, { data: aRes }] = await Promise.all([
                api.get("/links"),
                api.get("/analytics/me"),
            ]);
            setLinks(linksRes.items || []);
            setAnalytics(aRes);
        } catch (e) { console.error(e); }
    };

    useEffect(() => { if (user) load(); }, [user]);

    // Auto-activate a pending preview if the user just came back from Google/Emergent OAuth with ?pending=<id>
    useEffect(() => {
        const pendingId = searchParams.get("pending");
        if (!user || !pendingId) return;
        (async () => {
            try {
                const { data } = await api.post(`/preview/${encodeURIComponent(pendingId)}/activate`);
                toast.success(`Activated /${data.slug} — it's yours forever now.`);
                searchParams.delete("pending");
                setSearchParams(searchParams, { replace: true });
                load();
            } catch (e) {
                toast.error(e.response?.data?.detail || "Could not activate that preview link");
                searchParams.delete("pending");
                setSearchParams(searchParams, { replace: true });
            }
        })();
        // eslint-disable-next-line
    }, [user, searchParams.get("pending")]);

    const onCreated = (link) => {
        setResult(link);
        load();
    };

    const remove = async (id) => {
        if (!window.confirm("Delete this link and all its analytics?")) return;
        await api.delete(`/links/${id}`);
        load();
    };

    const copy = async (url, id) => {
        await navigator.clipboard.writeText(url);
        setCopiedId(id);
        setTimeout(() => setCopiedId(null), 1400);
    };

    const submitBulk = async () => {
        const urls = bulkText.split("\n").map((s) => s.trim()).filter(Boolean);
        if (!urls.length) return;
        await api.post("/links/bulk", { urls });
        setBulkText("");
        setBulkOpen(false);
        load();
    };

    if (loading || !user) {
        return <div className="flex min-h-screen items-center justify-center font-mono text-sm text-muted-foreground">Loading…</div>;
    }

    return (
        <div className="App min-h-screen">
            <Nav />
            <div className="mx-auto max-w-7xl px-6 py-10">
                <div className="mb-8 flex flex-col justify-between gap-4 sm:flex-row sm:items-end">
                    <div>
                        <div className="text-xs uppercase tracking-widest text-muted-foreground">Dashboard</div>
                        <h1 className="mt-2 font-display text-4xl font-bold tracking-tighter" data-testid="dashboard-heading">
                            Hey, {user.name || user.email.split("@")[0]}
                        </h1>
                    </div>
                    <div className="flex gap-2">
                        <Dialog open={bulkOpen} onOpenChange={setBulkOpen}>
                            <DialogTrigger asChild>
                                <Button variant="outline" data-testid="open-bulk-dialog-btn">
                                    <Sparkles className="mr-2 h-4 w-4" /> Bulk shorten
                                </Button>
                            </DialogTrigger>
                            <DialogContent>
                                <DialogHeader>
                                    <DialogTitle>Bulk shorten URLs</DialogTitle>
                                </DialogHeader>
                                <Textarea
                                    rows={8}
                                    placeholder={"https://example.com/one\nhttps://example.com/two"}
                                    value={bulkText}
                                    onChange={(e) => setBulkText(e.target.value)}
                                    className="font-mono text-sm"
                                    data-testid="bulk-textarea"
                                />
                                <Button onClick={submitBulk} className="w-full" data-testid="submit-bulk-btn">
                                    Shorten {bulkText.split("\n").filter((s) => s.trim()).length} URLs
                                </Button>
                            </DialogContent>
                        </Dialog>
                    </div>
                </div>

                {/* Metrics */}
                <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                    <MetricCard label="Your links" value={analytics?.my_links ?? "—"} icon={LinkIcon} testId="metric-my-links" />
                    <MetricCard label="Total clicks" value={analytics?.my_clicks ?? "—"} icon={MousePointerClick} testId="metric-my-clicks" />
                    <MetricCard label="Last 30 days" value={(analytics?.timeseries || []).reduce((a, b) => a + b.count, 0)} icon={BarChart3} testId="metric-30days" />
                    <MetricCard label="Account" value={user.role === "admin" ? "Admin" : "Standard"} icon={Sparkles} testId="metric-account" />
                </div>

                {/* Quick shorten */}
                <div className="mt-10 grid grid-cols-1 gap-8 lg:grid-cols-3">
                    <div className="lg:col-span-2">
                        <h2 className="mb-4 font-display text-xl font-bold">Quick shorten</h2>
                        <ShortenerForm onCreated={onCreated} />
                        {result && <ResultCard link={result} />}
                    </div>
                    <div>
                        <h2 className="mb-4 font-display text-xl font-bold">Clicks over time</h2>
                        <div className="border border-border bg-card p-4">
                            <TimeSeriesChart data={analytics?.timeseries || []} />
                        </div>
                    </div>
                </div>

                {/* Links table */}
                <div className="mt-14">
                    <div className="mb-4 flex items-end justify-between">
                        <h2 className="font-display text-xl font-bold">Your links</h2>
                        <div className="font-mono text-xs text-muted-foreground">{links.length} total</div>
                    </div>
                    <div className="overflow-x-auto border border-border bg-card">
                        <Table data-testid="links-table">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Short</TableHead>
                                    <TableHead>Destination</TableHead>
                                    <TableHead className="text-right">Clicks</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {links.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={4} className="py-8 text-center text-sm text-muted-foreground">
                                            No links yet — shorten one above ✦
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    links.map((l) => (
                                        <TableRow key={l.link_id} data-testid={`link-row-${l.slug}`}>
                                            <TableCell>
                                                <Link to={`/link/${l.link_id}`} className="font-mono text-primary hover:underline">
                                                    /{l.slug}
                                                </Link>
                                            </TableCell>
                                            <TableCell className="max-w-[280px] truncate font-mono text-xs text-muted-foreground">
                                                {l.long_url}
                                            </TableCell>
                                            <TableCell className="text-right font-mono">{l.click_count || 0}</TableCell>
                                            <TableCell className="text-right">
                                                <div className="flex justify-end gap-1">
                                                    <Button size="icon" variant="ghost" onClick={() => copy(l.short_url, l.link_id)} aria-label="Copy" data-testid={`copy-${l.slug}`}>
                                                        {copiedId === l.link_id ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                                                    </Button>
                                                    <Button size="icon" variant="ghost" onClick={() => setShareUrl(l.short_url)} aria-label="Share" data-testid={`share-${l.slug}`}>
                                                        <Share2 className="h-4 w-4" />
                                                    </Button>
                                                    <Button size="icon" variant="ghost" asChild aria-label="Analytics" data-testid={`analytics-${l.slug}`}>
                                                        <Link to={`/link/${l.link_id}`}><BarChart3 className="h-4 w-4" /></Link>
                                                    </Button>
                                                    <Button size="icon" variant="ghost" onClick={() => remove(l.link_id)} aria-label="Delete" data-testid={`delete-${l.slug}`}>
                                                        <Trash2 className="h-4 w-4" />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            </div>

            <ShareModal open={!!shareUrl} onOpenChange={(o) => !o && setShareUrl(null)} shortUrl={shareUrl} />
        </div>
    );
}

function MetricCard({ label, value, icon: Icon, testId }) {
    return (
        <div className="border border-border bg-card p-5" data-testid={testId}>
            <div className="flex items-center justify-between">
                <div className="text-xs uppercase tracking-widest text-muted-foreground">{label}</div>
                <Icon className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
            </div>
            <div className="mt-3 font-display text-3xl font-bold">{value}</div>
        </div>
    );
}
